import { FormEvent, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { BATCH_NAME_MAX_LENGTH, sanitizeBatchName } from '../sanitizeBatchName'

import { Dialog, FoundationNameWithFlag } from '@percent/admin-dashboard/common/components'
import { useFoundations } from '@percent/admin-dashboard/common/hooks'
import { CreateDisbursementModalProps } from './CreateDisbursementModal.types'
import {
  AsyncSelect,
  Box,
  Button,
  DateInput,
  FormField,
  ModalContent,
  Icon,
  Typography,
  Select,
  SelectOption,
  Radio,
  TextInput
} from '@percent/lemonade'
import styles from './CreateDisbursementModal.module.scss'
import { dayJS } from '@percent/admin-dashboard/common/library/utility/date'
import { useToast } from '@percent/admin-dashboard/containers/toast/ToastContext'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { DisbursementType } from '@percent/admin-dashboard/api/actions/disbursements/disbursements.types'
import { usePartnerSearchQuery } from './usePartnerSearchQuery/usePartnerSearchQuery'
import {
  usePostHogFeatureFlag,
  POSTHOG_FEATURE_FLAGS
} from '@percent/admin-dashboard/common/hooks/usePostHogFeatureFlag'

const DATE_FORMAT = 'DD MMM YYYY'

type DisbursementMethod = 'bank_transfer' | 'direct_to_campaign' | 'card' | 'cheque'
type BatchAggregationType = 'standalone' | 'bundled'

export function CreateDisbursementModal({
  openModal,
  onClose,
  refreshDisbursementsTable
}: CreateDisbursementModalProps) {
  const disbursementsBatchPartnerFilterFeatureFlag = usePostHogFeatureFlag(
    POSTHOG_FEATURE_FLAGS.AD_DISBURSEMENTS_BATCH_PARTNER_FILTER
  )
  const donationStandaloneFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.AD_DONATION_STANDALONE)

  const { t } = useTranslation()
  const { foundations } = useFoundations()
  const { disbursementsService } = useServices()
  const addToast = useToast()
  const [batchEndDate, setBatchEndDate] = useState(dayJS().subtract(1, 'day').endOf('day'))
  const [selectedFoundationId, setSelectedFoundationId] = useState('')
  const [batchName, setBatchName] = useState('')
  const [disbursementMethodOption, setDisbursementMethodOption] = useState<SelectOption | undefined>({
    value: 'bank_transfer',
    label: t('dialog.disbursement.form.typeOfDisbursementOption.standard')
  })
  const [selectedPartnerName, setSelectedPartnerName] = useState<string | undefined>(undefined)
  const [selectedPartnerId, setSelectedPartnerId] = useState<string | undefined>(undefined)
  const [batchAggregationType, setBatchAggregationType] = useState<BatchAggregationType>('bundled')

  const { searchResults, query, isLoading: isPartnersLoading, setQuery } = usePartnerSearchQuery()

  const foundationsOptions = useMemo(() => {
    return Object.values(foundations || {}).map(foundation => ({
      title: <FoundationNameWithFlag countryCode={foundation.countryCode} name={foundation.name} />,
      value: foundation.id
    }))
  }, [foundations])

  const disbursementMethodOptions = useMemo<{ title: string; value: DisbursementMethod }[]>(() => {
    return [
      {
        title: t('dialog.disbursement.form.typeOfDisbursementOption.standard'),
        value: 'bank_transfer'
      },
      {
        title: t('dialog.disbursement.form.typeOfDisbursementOption.directToCampaign'),
        value: 'direct_to_campaign'
      },
      {
        title: t('dialog.disbursement.form.typeOfDisbursementOption.card'),
        value: 'card'
      },
      {
        title: t('dialog.disbursement.form.typeOfDisbursementOption.cheque'),
        value: 'cheque'
      }
    ]
  }, [t])

  const resetValues = () => {
    setBatchEndDate(dayJS().subtract(1, 'day').endOf('day'))
    setSelectedFoundationId('')
    setBatchName('')
    setBatchAggregationType('bundled')
    setSelectedPartnerName(undefined)
    setSelectedPartnerId(undefined)
  }

  const handleClose = () => {
    resetValues()
    onClose()
  }

  function onPartnerChange(partnerId: string, partnerName: string) {
    setSelectedPartnerId(partnerId)
    setSelectedPartnerName(partnerName)
  }

  function onQueryChange(q: string) {
    setQuery(q)
  }

  function onBatchAggregationTypeChange(value: BatchAggregationType) {
    setBatchAggregationType(value)

    if (value === 'bundled') {
      setSelectedPartnerName(undefined)
      setSelectedPartnerId(undefined)
    }
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    try {
      const disbursementMethod = disbursementMethodOption?.value as DisbursementMethod
      const { type, paymentMethodTypes } =
        disbursementMethod === 'direct_to_campaign'
          ? { type: DisbursementType.EXTERNAL, paymentMethodTypes: undefined }
          : { type: DisbursementType.INTERNAL, paymentMethodTypes: [disbursementMethod] }

      await disbursementsService.createDisbursementBatch({
        type,
        paymentMethodTypes,
        foundationId: selectedFoundationId,
        endDate: batchEndDate.toISOString(),
        partnerId: batchAggregationType === 'standalone' ? selectedPartnerId : undefined,
        batchAggregationType,
        name: batchName.trim() || undefined
      })

      addToast(t('toast.createDisbursementBatchSuccess'), 'success')
      refreshDisbursementsTable?.()
      handleClose()
    } catch (e) {
      addToast(
        t(
          `toast.${
            e.response.status === 400 ? 'createDisbursementBatchNoOrganisationsError' : 'createDisbursementBatchError'
          }`
        ),
        'error'
      )
    }
  }

  return (
    <Dialog headerTitle={t('dialog.disbursement.header')} openModal={openModal} onClose={handleClose}>
      <ModalContent>
        <form onSubmit={handleSubmit}>
          <Box className={styles.formFieldWrapper}>
            <FormField label={t('dialog.disbursement.form.typeOfDisbursement')} data-testid="disbursementMethod">
              <Select
                data-testid="reassignedReasonSelect"
                placeholder={t('dialog.disbursement.form.selectTypeOfDisbursement')}
                options={disbursementMethodOptions.map(option => ({
                  label: option.title,
                  value: option.value
                }))}
                defaultValue={disbursementMethodOption}
                onChange={({ value }) =>
                  setDisbursementMethodOption({
                    label: disbursementMethodOptions.find(option => option.value === value)?.title || '',
                    value
                  })
                }
              />
            </FormField>
          </Box>
          {donationStandaloneFeatureFlag && (
            <Box className={styles.formFieldWrapper}>
              <label className={styles.label}>{t('dialog.disbursement.form.aggregationType.title')}</label>
              <Box className={styles.radioGroup}>
                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
                <div
                  className={`${styles.radioCard} ${batchAggregationType === 'bundled' ? styles.checked : ''}`}
                  onClick={() => onBatchAggregationTypeChange('bundled')}
                >
                  <Radio
                    id="batchAggregationType-bundled"
                    name="batchAggregationType"
                    value="bundled"
                    label={t('dialog.disbursement.form.aggregationType.bundled')}
                    checked={batchAggregationType === 'bundled'}
                  />
                </div>
                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
                <div
                  className={`${styles.radioCard} ${batchAggregationType === 'standalone' ? styles.checked : ''}`}
                  onClick={() => onBatchAggregationTypeChange('standalone')}
                >
                  <Radio
                    id="batchAggregationType-standalone"
                    name="batchAggregationType"
                    value="standalone"
                    label={t('dialog.disbursement.form.aggregationType.standalone')}
                    checked={batchAggregationType === 'standalone'}
                  />
                </div>
              </Box>
            </Box>
          )}
          {disbursementsBatchPartnerFilterFeatureFlag && batchAggregationType === 'standalone' && (
            <Box className={styles.formFieldWrapper}>
              <FormField label={t('dialog.disbursement.form.partner')} data-testid="partnerId" necessity="optional">
                <AsyncSelect
                  name="partnerId"
                  placeholder={t('typography.search.partner')}
                  onChange={e => {
                    onPartnerChange(e?.value || '', e?.label || '')
                  }}
                  options={searchResults}
                  setQuery={e => {
                    onQueryChange(e)
                  }}
                  query={query}
                  loading={isPartnersLoading}
                  loadingText="loading"
                  noResultsFoundText={t('errorMessage.noResult')}
                  data-testid="partnerSearch"
                  defaultValue={searchResults.find(option => option.value === selectedPartnerName)}
                />
              </FormField>
            </Box>
          )}
          <Box className={styles.formFieldWrapper}>
            {foundationsOptions && (
              <FormField label={t('dialog.disbursement.form.foundation')} data-testid="foundation">
                <Select
                  data-testid="foundationSelect"
                  placeholder={t('dialog.disbursement.form.selectFoundation')}
                  options={foundationsOptions.map(option => ({
                    label: option.title as unknown as string,
                    value: option.value
                  }))}
                  defaultValue={{
                    label:
                      (foundationsOptions.find(option => option.value === selectedFoundationId)
                        ?.title as unknown as string) || '',
                    value: selectedFoundationId
                  }}
                  onChange={({ value }) => setSelectedFoundationId(value)}
                />
              </FormField>
            )}
          </Box>
          <Box className={styles.formFieldWrapper}>
            <FormField label={t('dialog.disbursement.form.endDate')} data-testid="endDate">
              <DateInput
                name="batchEndDate"
                value={batchEndDate.toDate()}
                onChange={date =>
                  setBatchEndDate(date ? dayJS(date).endOf('day') : dayJS().subtract(1, 'day').endOf('day'))
                }
                maxDate={dayJS().subtract(1, 'day').toDate()}
                dateFormat={DATE_FORMAT}
                data-testid="date-picker"
                withinPopup
              />
            </FormField>
          </Box>
          <Box className={styles.formFieldWrapper}>
            <FormField label={t('typography.name')} data-testid="disbursementBatchName" necessity="optional">
              <TextInput
                name="disbursementBatchName"
                placeholder={t('dialog.disbursement.form.namePlaceholder')}
                value={batchName}
                maxLength={BATCH_NAME_MAX_LENGTH}
                onChange={event => setBatchName(sanitizeBatchName(event.target.value))}
              />
            </FormField>
          </Box>
          <Box className={styles.info}>
            <Icon name="info" color="gray500" size={6} />
            <Typography variant="small" className={styles.infoText}>
              {t('dialog.disbursement.info')}
            </Typography>
          </Box>
          <Box className={styles.buttonsWrapper}>
            <Button
              size="large"
              type="submit"
              data-testid="createDisbursementButton"
              disabled={!selectedFoundationId.length || !disbursementMethodOption?.value?.length || !batchEndDate}
            >
              {t('button.create')}
            </Button>
            <Button onClick={handleClose} variant="secondary" size="large" type="button">
              {t('button.cancel')}
            </Button>
          </Box>
        </form>
      </ModalContent>
    </Dialog>
  )
}
